import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { getMemberDetail, type MemberProfile } from "../api/members";
import { getFinancialOverview, type FinancialOverview } from "../api/payments";
import type { SessionCapabilities } from "../api/sessionContext";
import { isOperationalUser, roleLabel } from "../auth/sessionRole";
import { useAuth } from "../auth/AuthContext";
import { DashboardHero, type DashboardHeroMode } from "../components/DashboardHero";
import { MembershipCard, MembershipCardSkeleton } from "../components/MembershipCard";

type MembershipCardState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; profile: MemberProfile }
  | { status: "error" };

interface OperationalModuleDef {
  readonly id: string;
  readonly capability: keyof SessionCapabilities;
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly to: string;
  readonly actionLabel: string;
}

const OPERATIONAL_MODULE_DEFS: readonly OperationalModuleDef[] = [
  {
    id: "members-search",
    capability: "read_members",
    eyebrow: "Secretaria e DAF",
    title: "Consulta de associados",
    description: "Pesquise cadastros, consulte a ficha institucional e acompanhe o quadro associativo.",
    to: "/associados",
    actionLabel: "Abrir associados",
  },
  {
    id: "members-create",
    capability: "create_members",
    eyebrow: "Secretaria Geral",
    title: "Novo associado",
    description: "Cadastre novos associados e emita credenciais de primeiro acesso institucional.",
    to: "/associados/novo",
    actionLabel: "Cadastrar associado",
  },
  {
    id: "mfa-recovery-request",
    capability: "request_mfa_recovery",
    eyebrow: "Segurança de Acesso",
    title: "Recuperação presencial de MFA",
    description: "Inicie solicitações presenciais para redefinição assistida de segundo fator.",
    to: "/mfa/recuperacao/solicitar",
    actionLabel: "Iniciar recuperação",
  },
  {
    id: "mfa-recovery-approve",
    capability: "approve_mfa_recovery",
    eyebrow: "Governança de Acesso",
    title: "Aprovações MFA",
    description: "Analise e delibere sobre solicitações presenciais pendentes de recuperação.",
    to: "/mfa/recuperacao/aprovar",
    actionLabel: "Abrir aprovações MFA",
  },
  {
    id: "access-management",
    capability: "manage_access",
    eyebrow: "Administração institucional",
    title: "Gestão de acessos",
    description: "Gerencie perfis operacionais, acompanhe credenciais e concentre a liberação de acessos.",
    to: "/acessos",
    actionLabel: "Abrir gestão de acessos",
  },
];

function financialMessage(overview: FinancialOverview | null): string {
  if (overview === null) return "Consultando somente obrigações confirmadas pelo serviço institucional…";
  const invoices = overview.invoices ?? [];
  const hasOpenInvoice = invoices.some((invoice) => invoice.status === "pending" || invoice.status === "overdue");
  if (hasOpenInvoice) return "Há uma obrigação em aberto. Consulte as instruções atuais para pagar com segurança.";
  if (invoices.some((invoice) => invoice.status === "paid")) return "Seu último registro financeiro confirmado está pago.";
  return "Nenhuma obrigação em aberto foi registrada para este acesso.";
}

function AssociateFinancialStatus({
  memberId,
  accessToken,
  expireSession,
}: {
  memberId: string;
  accessToken: string;
  expireSession: () => void;
}) {
  const [overview, setOverview] = useState<FinancialOverview | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getFinancialOverview(accessToken)
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch((error: unknown) => {
        if (!cancelled && error instanceof ApiError && error.status === 401) expireSession();
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, expireSession, memberId]);

  return (
    <section className="dashboard__financial-status dashboard__financial-status--priority" aria-labelledby="dashboard-financial-title">
      <div className="dashboard__financial-header">
        <p className="dashboard__eyebrow">Vida associativa</p>
        <h2 id="dashboard-financial-title">Resumo financeiro</h2>
        <p role="status" aria-live="polite">{financialMessage(overview)}</p>
      </div>
      <div className="dashboard__financial-actions">
        <Link className="dashboard__financial-link" to="/contribuicoes">
          Consultar mensalidades e contribuições
        </Link>
      </div>
    </section>
  );
}

function AssociateActions({ memberId }: { memberId: string }) {
  return (
    <section className="dashboard__actions" aria-labelledby="dashboard-actions-title">
      <div className="dashboard__actions-header">
        <p className="dashboard__eyebrow">Sua conta</p>
        <h2 id="dashboard-actions-title">Ações rápidas</h2>
        <p>Atualize seu autenticador e consulte suas informações cadastrais pelo fluxo protegido do portal.</p>
      </div>
      <div className="dashboard__actions-group">
        <Link className="dashboard__action-link dashboard__action-link--secondary" to={`/associados/${memberId}`}>
          Meus dados cadastrais
        </Link>
        <Link className="dashboard__action-link" to="/mfa/troca">
          Trocar autenticador
        </Link>
      </div>
    </section>
  );
}

function DashboardNoticeBoard() {
  return (
    <section className="dashboard__notices" aria-labelledby="dashboard-notices-title">
      <div className="dashboard__notices-header">
        <p className="dashboard__eyebrow">Vida associativa</p>
        <h2 id="dashboard-notices-title">Comunicações e agenda</h2>
      </div>
      <div className="dashboard__notices-empty">
        <p>Comunicados e agenda serão exibidos aqui quando o serviço institucional estiver disponível.</p>
      </div>
    </section>
  );
}

function MembershipCardBlock({ state }: { state: MembershipCardState }) {
  return (
    <>
      {state.status === "loading" && <MembershipCardSkeleton />}
      {state.status === "ready" && <MembershipCard profile={state.profile} />}
      {state.status === "error" && (
        <p className="dashboard__context-status" role="status">Não foi possível carregar a carteirinha institucional agora.</p>
      )}
    </>
  );
}

/**
 * DECISÃO ARQUITETURAL SOBRE CAPABILITIES FINANCEIRAS:
 * `read_payments`, `record_payment` e `adjust_payment` classificam a sessão como operacional,
 * mas operam de modo contextual por associado (/associados/:memberId/financeiro).
 * Não existe rota institucional consolidada (/financeiro global) até que o serviço correspondente
 * seja disponibilizado. Para evitar um estado vazio enganoso, exibimos este módulo explicitamente.
 */
function OperationalFinancialModule({ capabilities }: { capabilities: SessionCapabilities | undefined }) {
  const canReadMembers = Boolean(capabilities?.read_members);

  return (
    <article className="dashboard__module dashboard__module--finance" aria-labelledby="finance-ops-title">
      <p className="dashboard__eyebrow">Gestão Financeira</p>
      <h3 id="finance-ops-title">Operações financeiras</h3>
      <p>
        Consultas e conciliações de mensalidades são realizadas na ficha individual do associado.
        {canReadMembers
          ? " Acesse a consulta de associados para localizar o cadastro desejado."
          : " O painel consolidado será disponibilizado com a integração do serviço institucional."}
      </p>
      {canReadMembers ? (
        <Link className="dashboard__module-cta" to="/associados">
          Ir para associados
        </Link>
      ) : (
        <p className="dashboard__module-note">Painel consolidado pendente de serviço institucional.</p>
      )}
    </article>
  );
}

function OperationalModulesList({ capabilities }: { capabilities: SessionCapabilities | undefined }) {
  const allowed = capabilities
    ? OPERATIONAL_MODULE_DEFS.filter((item) => Boolean(capabilities[item.capability]))
    : [];
  const hasFinancialCaps = Boolean(
    capabilities?.read_payments || capabilities?.record_payment || capabilities?.adjust_payment,
  );

  if (allowed.length === 0 && !hasFinancialCaps) {
    return <p className="dashboard__empty-state">Este acesso não possui módulos operacionais liberados.</p>;
  }

  return (
    <section className="dashboard__modules" aria-labelledby="dashboard-modules-title">
      <h2 id="dashboard-modules-title" className="dashboard__modules-title">Módulos disponíveis</h2>
      {allowed.map((mod) => (
        <article key={mod.id} className={`dashboard__module dashboard__module--${mod.id}`} aria-labelledby={`${mod.id}-title`}>
          <p className="dashboard__eyebrow">{mod.eyebrow}</p>
          <h3 id={`${mod.id}-title`}>{mod.title}</h3>
          <p>{mod.description}</p>
          <Link className="dashboard__module-cta" to={mod.to}>
            {mod.actionLabel}
          </Link>
        </article>
      ))}
      {hasFinancialCaps && <OperationalFinancialModule capabilities={capabilities} />}
    </section>
  );
}

function OperationalSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      navigate("/associados");
      return;
    }
    navigate("/associados", { state: { search: query } });
  };

  return (
    <section className="dashboard__search-card" aria-labelledby="dashboard-search-title">
      <div className="dashboard__search-header">
        <p className="dashboard__eyebrow">Atendimento e Balcão</p>
        <h2 id="dashboard-search-title">Localizar associado</h2>
        <p>Busque imediatamente por nome civil, nome preferido, CPF ou usuário para abrir a ficha institucional.</p>
      </div>
      <form className="dashboard__search-form" onSubmit={handleSearch} role="search">
        <label htmlFor="operator-member-search" className="visually-hidden">
          Buscar associado por nome, CPF ou usuário
        </label>
        <div className="dashboard__search-input-group">
          <span className="dashboard__search-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
              <circle cx="10.8" cy="10.8" r="5.8" stroke="currentColor" strokeWidth="2" />
              <path d="m15.2 15.2 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input
            id="operator-member-search"
            type="search"
            className="dashboard__search-input"
            placeholder="Digite o nome, CPF ou usuário do associado…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" className="dashboard__search-btn">
            Buscar cadastro
          </button>
        </div>
      </form>
    </section>
  );
}

function OperationalDashboardContent({
  sessionContextStatus,
  capabilities,
}: {
  sessionContextStatus: "loading" | "ready" | "unavailable";
  capabilities: SessionCapabilities | undefined;
}) {
  if (sessionContextStatus === "loading") {
    return <p className="dashboard__context-status" aria-live="polite">Carregando permissões institucionais…</p>;
  }

  if (sessionContextStatus === "unavailable") {
    return (
      <p className="dashboard__context-status" role="alert">
        Não foi possível confirmar os módulos desta sessão. Atualize a página para tentar novamente.
      </p>
    );
  }

  const canReadMembers = Boolean(capabilities?.read_members);

  return (
    <>
      {canReadMembers && <OperationalSearch />}
      <OperationalModulesList capabilities={capabilities} />
      <section className="dashboard__actions" aria-labelledby="dashboard-actions-title">
        <div className="dashboard__actions-header">
          <p className="dashboard__eyebrow">Sua conta</p>
          <h2 id="dashboard-actions-title">Segurança da conta</h2>
          <p>Atualize seu autenticador quando precisar, sempre pelo fluxo protegido do portal.</p>
        </div>
        <div className="dashboard__actions-group">
          <Link className="dashboard__action-link" to="/mfa/troca">
            Trocar autenticador
          </Link>
        </div>
      </section>
    </>
  );
}

function AssociateDashboardContent({
  membershipCard,
  memberId,
  accessToken,
  expireSession,
}: {
  membershipCard: MembershipCardState;
  memberId: string;
  accessToken: string;
  expireSession: () => void;
}) {
  return (
    <>
      <AssociateFinancialStatus
        memberId={memberId}
        accessToken={accessToken}
        expireSession={expireSession}
      />
      <div className="dashboard__associate-grid">
        <div className="dashboard__associate-primary">
          <MembershipCardBlock state={membershipCard} />
        </div>
        <div className="dashboard__associate-secondary">
          <AssociateActions memberId={memberId} />
        </div>
      </div>
      <DashboardNoticeBoard />
    </>
  );
}

function useAssociateMembershipCard(
  isAssociate: boolean,
  memberId: string | null,
  accessToken: string | null,
  expireSession: () => void,
): MembershipCardState {
  const [state, setState] = useState<MembershipCardState>({ status: "idle" });

  useEffect(() => {
    if (!isAssociate || !memberId || !accessToken) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    void getMemberDetail(accessToken, memberId)
      .then((profile) => {
        if (!cancelled) setState({ status: "ready", profile });
      })
      .catch((error) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          expireSession();
          return;
        }
        setState({ status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, expireSession, isAssociate, memberId]);

  return state;
}

function resolveHeroMode(isReady: boolean, isOperational: boolean): DashboardHeroMode {
  if (!isReady) return "neutral";
  return isOperational ? "operational" : "associate";
}

function resolveDashboardContext(state: ReturnType<typeof useAuth>["state"]) {
  if (state.status !== "authenticated") {
    return {
      isAuthenticated: false,
      capabilities: undefined,
      memberId: null,
      username: null,
      isAssociate: false,
      accessToken: null,
      sessionContextStatus: "unavailable" as const,
      heroMode: "neutral" as const,
      roleLabel: "Acesso institucional",
    };
  }

  const capabilities = state.sessionContext?.capabilities;
  const memberId = state.sessionContext?.member_id ?? null;
  const isOperational = isOperationalUser(capabilities);
  const isReady = state.sessionContextStatus === "ready";
  const isAssociate = isReady && Boolean(memberId) && !isOperational;

  return {
    isAuthenticated: true,
    capabilities,
    memberId,
    username: state.username,
    isAssociate,
    accessToken: state.accessToken,
    sessionContextStatus: state.sessionContextStatus,
    heroMode: resolveHeroMode(isReady, isOperational),
    roleLabel: roleLabel(capabilities, Boolean(memberId)),
  };
}

function resolveMemberDisplayName(
  membershipCard: MembershipCardState,
  username: string | null | undefined,
): string | null {
  if (membershipCard.status === "ready") {
    return membershipCard.profile.preferred_name?.trim() || membershipCard.profile.full_name;
  }
  return username ?? null;
}

export function DashboardPage() {
  const { state, expireSession } = useAuth();
  const context = resolveDashboardContext(state);

  const membershipCard = useAssociateMembershipCard(
    context.isAssociate,
    context.memberId,
    context.accessToken,
    expireSession,
  );

  if (!context.isAuthenticated || !context.accessToken) return null;

  const memberDisplayName = context.isAssociate
    ? resolveMemberDisplayName(membershipCard, context.username)
    : null;

  return (
    <main className="dashboard">
      <DashboardHero
        mode={context.heroMode}
        roleLabel={context.roleLabel}
        memberName={memberDisplayName}
      />

      {context.isAssociate && context.memberId ? (
        <AssociateDashboardContent
          membershipCard={membershipCard}
          memberId={context.memberId}
          accessToken={context.accessToken}
          expireSession={expireSession}
        />
      ) : (
        <OperationalDashboardContent
          sessionContextStatus={context.sessionContextStatus}
          capabilities={context.capabilities}
        />
      )}
    </main>
  );
}
